import React, { useCallback, useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { Col, Form, InputGroup, ListGroup, OverlayTrigger, Popover, Row } from 'react-bootstrap-5';
import { createTag, deleteTag, fetchTags, updateTag } from '../../helpers/prospect_api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faPencil, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { debounce } from 'lodash';
import { displayToast } from '../../helpers/display_toast';
import pluralize from 'pluralize';
import TagModal from './tag_modal';
import EditTagPopover from './edit_tag_popover';
import type { Tag } from 'bundles/connect/types';

export default function TagSettings() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [searchString, setSearchString] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tagForModal, setTagForModal] = useState('');
  const [editPopover, setEditPopover] = useState<{
    show: boolean;
    anchor: HTMLElement | null;
    tag: Tag | null;
  }>({
    show: false,
    anchor: null,
    tag: null,
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchTags(searchString).then((response) => {
      setTags(response['connect/tags']);
    });
  }, []);

  const debouncedSearch = useCallback(
    debounce((inputString) => {
      fetchTags(inputString).then(response => setTags(response['connect/tags']));
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchString);
  }, [searchString]);

  const handleRemove = (tag: Tag) => {
    if (confirm(`Delete tag "${tag.name}"?`)) {
      deleteTag(tag.id).then(() => {
        setTags(tags.filter(t => t.id != tag.id));
      });
    }
  };

  const handleUpdate = (id: number, name: string) => {
    updateTag(id, name).then((response) => {
      const updatedTag = response['connect/tag'];
      setTags(prevTags => prevTags.map(tag => tag.id === updatedTag.id ? updatedTag : tag));
      displayToast('Tag has been updated!', 'success');
      setEditPopover(prev => ({ ...prev, show: false }));
    }).catch(() => displayToast('Something went wrong', 'error'));
  };

  const handleEdit = (e: React.MouseEvent<HTMLElement>, tag: Tag) => {
    setEditPopover({
      show: true,
      anchor: e.currentTarget,
      tag: tag
    });
  };

  const handleCreate = () => {
    createTag(tagInput).then((response) => {
      setTags([
        ...tags,
        response['connect/tag']
      ]);
      setShowOverlay(false);
      displayToast('Tag Created!', 'success');
    });
  };

  const handleOpenModal = useCallback((tag) => {
    setShowModal(true);
    setTagForModal(tag);
  }, []);

  const popover = (
    <Popover className="border" style={{ width: '400px' }} onClick={e => e.stopPropagation()}>
      <Popover.Body className="px-0 pb-0">
        <div className=" px-3 pb-2 border-bottom">
          <h6 className="fw-bolder m-0">New Tag</h6>
        </div>
        <Form.Group className="mb-2 px-3 pb-2">
          <Form.Label>Tag Name</Form.Label>
          <Form.Control type="text" onChange={e => setTagInput(e.target.value)} />
        </Form.Group>
        <div className="border-top d-flex align-items-center justify-content-end p-2">
          <Button label="Cancel" variant="ghost" onClick={() => setShowOverlay(false)} />
          <Button label="Create Tag" variant="secondary" onClick={handleCreate} />
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <Row className="align-items-center mb-8">
        <Col><h5 className="fw-semibold">Tags</h5></Col>
        <Col xs="auto">
          <OverlayTrigger
            overlay={popover}
            placement="bottom"
            rootClose
            show={showOverlay}
            trigger="click"
            onToggle={show => setShowOverlay(show)}
          >
            <Button icon={faPlus} label="New Tag" variant="secondary" onClick={() => { }} />
          </OverlayTrigger>
        </Col>
      </Row>
      <Form.Group className="mb-2">
        <Form.Label>Search tags</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search tags..."
            type="text"
            value={searchString}
            onChange={e => setSearchString(e.target.value)}
          />
        </InputGroup>
      </Form.Group>
      <ListGroup>
        {tags.map(tag => (
          <ListGroup.Item key={tag.name}>
            <Row className="align-items-center d-flex flex-grow-1 w-100">
              <Col className="text-truncate">
                <div
                  className="fw-bolder d-inline"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenModal(tag.name)}
                >
                  {tag.name}
                </div>
                <small className="text-muted">
                  <div className="d-flex flex-wrap column-gap-1">
                    <div>
                      {tag.tagCount} {pluralize('Record', tag.tagCount)} tagged
                    </div>
                    <span>•</span>
                    <div>
                      Created on {tag.createdAt}
                    </div>
                    <span>•</span>
                    <div>
                      Last modified on {tag.updatedAt}
                    </div>
                  </div>
                </small>
              </Col>
              <Col xs="auto">
                <Button
                  className="text-link"
                  icon={faPencil}
                  label="Edit"
                  variant="ghost"
                  onClick={e => handleEdit(e, tag)}
                />
                <Button
                  className="text-danger"
                  icon={faTrash}
                  label="Delete"
                  variant="ghost"
                  onClick={() => handleRemove(tag)}
                />
              </Col>
            </Row>
          </ListGroup.Item>
        ))}
      </ListGroup>
      <TagModal showModal={showModal} tagForModal={tagForModal} onHide={() => setShowModal(false)} />
      {editPopover.show && editPopover.anchor && editPopover.tag && (
        <EditTagPopover
          anchor={editPopover.anchor}
          tag={editPopover.tag}
          onCancel={() => setEditPopover(prev => ({ ...prev, show: false }))}
          onUpdate={handleUpdate}
        />
      )}
    </>
  );
}
